/**
 * Protected areas (national parks, conservancies) overlay.
 * Separated from the regulatory airspace layer into the "Drone Awareness" group.
 *
 * Data source: public/data/drone-awareness-parks.json
 */
import mapboxgl from 'mapbox-gl';
import { fetchJSON } from '../utils/fetch-data';

const SOURCE_ID = 'drone-parks';
const LAYER_FILL = 'drone-parks-fill';
const LAYER_LINE = 'drone-parks-line';
const ALL_LAYERS = [LAYER_FILL, LAYER_LINE];

let geojsonData: GeoJSON.FeatureCollection | null = null;

async function loadData(): Promise<GeoJSON.FeatureCollection> {
  if (geojsonData) return geojsonData;
  geojsonData = await fetchJSON<GeoJSON.FeatureCollection>('/data/drone-awareness-parks.json');
  return geojsonData;
}

export async function addDroneAwarenessParksLayer(map: mapboxgl.Map): Promise<void> {
  const data = await loadData();
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, { type: 'geojson', data });

  // Place below airspace zones if they exist, or below airports
  const beforeLayer = map.getLayer('airspace-zones') ? 'airspace-zones'
    : map.getLayer('airport-large') ? 'airport-large' : undefined;

  map.addLayer({
    id: LAYER_FILL, type: 'fill', source: SOURCE_ID, minzoom: 4,
    paint: {
      'fill-color': 'rgba(249, 168, 37, 0.22)',
      'fill-opacity': 1,
    },
  }, beforeLayer);

  map.addLayer({
    id: LAYER_LINE, type: 'line', source: SOURCE_ID, minzoom: 4,
    paint: {
      'line-color': '#f9a825',
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.8, 8, 1.6, 12, 2.0] as any,
      'line-opacity': 0.90,
    },
  }, beforeLayer);

  // Click + hover handlers
  map.on('click', LAYER_FILL, onParkClick);
  map.on('mouseenter', LAYER_FILL, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', LAYER_FILL, () => { map.getCanvas().style.cursor = ''; });
}

export function removeDroneAwarenessParksLayer(map: mapboxgl.Map): void {
  map.off('click', LAYER_FILL, onParkClick);
  for (const id of ALL_LAYERS) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

let activePopup: mapboxgl.Popup | null = null;

function onParkClick(e: mapboxgl.MapLayerMouseEvent) {
  if (!e.features?.length) return;
  e.originalEvent.stopPropagation();

  const props = e.features[0].properties as Record<string, string>;

  activePopup?.remove();
  const typeLabel = props.type || 'Protected Area';
  activePopup = new mapboxgl.Popup({ closeButton: true, maxWidth: '240px' })
    .setLngLat(e.lngLat)
    .setHTML(`
      <div class="hz-popup">
        <div class="hz-popup-type" style="color:#f9a825">
          <span class="hz-icon">🌿</span> ${typeLabel}
        </div>
        ${props.name ? `<div class="hz-popup-name">${props.name}</div>` : ''}
        <div class="hz-popup-note">Drone operators must check local regulations before flying in protected areas.</div>
      </div>
    `)
    .addTo(e.target as mapboxgl.Map);
}
